import { useEffect } from 'react';
import { VideoEffectStore } from '../store/videoEffectModel';
import { BackgroundImage, BlurEffect } from '../videoEffectTypes';
import { useBlurEffect } from './useBlurEffect';
import { useCollaboratorPreferences } from './UseCollaboratorPreferences';
import { useVirtualBackground } from './useVirtualBackground';
import { usePreferenceHydration } from './usePreferenceHydration';
import { useVideoEffectDispatch } from './useVideoEffectDispatch';
import { useTrackChangeReapply } from './useTrackChangeReapply';

export interface UseVideoEffectReturn {
  applyBlur: (effect: BlurEffect) => Promise<void>;
  applyBackground: (background: BackgroundImage | null) => Promise<void>;
  removeAllEffects: () => Promise<void>;
  clearImageCache: () => void;
}

export const useVideoEffect = (assetsPath: string): UseVideoEffectReturn => {
  useCollaboratorPreferences();

  const setIsApplyingEffect = VideoEffectStore.useStoreActions((state) => state.setIsApplyingEffect);

  const blur = useBlurEffect(assetsPath);
  const background = useVirtualBackground(assetsPath);

  const { applyBlur, applyBackground, removeAllEffects, activeBackgroundImageRef } = useVideoEffectDispatch({
    blurApplyEffect: blur.applyEffect,
    backgroundApplyBackground: background.applyBackground,
  });

  const hydratedPreferences = usePreferenceHydration({
    applyBackground,
    applyBlur,
    removeAllEffects,
  });

  useTrackChangeReapply({
    isReady: hydratedPreferences !== null,
    activeBackgroundImageRef,
    applyBackground: background.applyBackground,
    applyBlur: blur.applyEffect,
  });

  useEffect(() => {
    setIsApplyingEffect(blur.isApplying || background.isApplying);
  }, [blur.isApplying, background.isApplying, setIsApplyingEffect]);

  return {
    applyBlur,
    applyBackground,
    removeAllEffects,
    clearImageCache: background.clearImageCache,
  };
};
